import { useState, useEffect } from "react";
import type { MetaFunction } from "@remix-run/node";
import { useTranslation } from "~/shared/utils/i18n";
import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";
import "~/features/dates/today-date.css";
import { motion } from 'framer-motion';
import { 
  FaCalendarAlt, 
  FaCopy, 
  FaCheck,
  FaGlobe,
  FaSun,
  FaMoon,
  FaClock
} from 'react-icons/fa';
import { AiFillHome } from 'react-icons/ai';
import { Link } from "@remix-run/react";
// @ts-expect-error - moment-hijri types not available
import moment from 'moment-hijri';

// ============================================================================
// SEO METADATA - تحسين محركات البحث
// ============================================================================

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('todayDate');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('todayDate');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "تاريخ اليوم الحالي - أسامة الزبيدي | التاريخ الهجري والميلادي بدقة | Today's Date - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "تاريخ اليوم الحالي - أسامة الزبيدي | التاريخ الهجري والميلادي بدقة" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/today-date" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "تاريخ اليوم الحالي - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "تاريخ اليوم الحالي - أسامة الزبيدي | التاريخ الهجري والميلادي بدقة" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "تاريخ اليوم الحالي - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/today-date" },
    { name: "alternate", content: "https://oz.sa/today-date", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/today-date", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/today-date", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// Unified color scheme matching the main page design
const dateColors = {
  primary: {
    light: 'bg-gradient-to-br from-purple-500 to-purple-600',
    dark: 'dark:from-purple-600 dark:to-purple-700',
    text: 'text-white',
    hover: 'hover:from-purple-600 hover:to-purple-700',
    shadow: 'shadow-purple-500/20'
  },
  secondary: {
    light: 'bg-gradient-to-br from-pink-500 to-pink-600',
    dark: 'dark:from-pink-600 dark:to-pink-700',
    text: 'text-white',
    hover: 'hover:from-pink-600 hover:to-pink-700',
    shadow: 'shadow-pink-500/20'
  },
  success: {
    light: 'bg-gradient-to-br from-green-500 to-green-600',
    dark: 'dark:from-green-600 dark:to-green-700',
    text: 'text-white',
    hover: 'hover:from-green-600 hover:to-green-700',
    shadow: 'shadow-green-500/20'
  },
  info: {
    light: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    dark: 'dark:from-indigo-600 dark:to-indigo-700',
    text: 'text-white',
    hover: 'hover:from-indigo-600 hover:to-indigo-700',
    shadow: 'shadow-indigo-500/20'
  }
};

// Date information interface
interface DateInfo {
  hijri: {
    day: string;
    month: string;
    year: string;
    fullDate: string;
    dayName: string;
    monthName: string;
  };
  gregorian: {
    day: string;
    month: string;
    year: string;
    fullDate: string;
    dayName: string;
    monthName: string;
  };
  time: string;
  timezone: string;
}

// Copy to clipboard function
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  } catch (error) {
    console.error('Failed to copy text: ', error);
    return false;
  }
};

// Date format component
function DateFormatCard({ 
  title, 
  date, 
  colorScheme, 
  // lang: 'ar' | 'en';
  dateInfo
}: {
  title: string;
  date: string;
  colorScheme: keyof typeof dateColors;
  // lang: 'ar' | 'en';
  dateInfo: DateInfo;
}) {
  const colors = dateColors[colorScheme];
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Generate date formats based on actual date
  const generateFormats = () => {
    const isHijri = title.includes('هجري') || title.includes('Hijri');
    const currentDate = isHijri ? dateInfo.hijri : dateInfo.gregorian;
    
    if (isHijri) {
      return [
        { 
          label: 'يوم/شهر/سنة', 
          format: 'DD/MM/YYYY', 
          example: `${currentDate.day}/${currentDate.month}/${currentDate.year}` 
        },
        { 
          label: 'يوم-شهر-سنة', 
          format: 'DD-MM-YYYY', 
          example: `${currentDate.day}-${currentDate.month}-${currentDate.year}` 
        },
        { 
          label: 'يوم.شهر.سنة', 
          format: 'DD.MM.YYYY', 
          example: `${currentDate.day}.${currentDate.month}.${currentDate.year}` 
        },
        { 
          label: 'يوم شهر سنة', 
          format: 'DD MM YYYY', 
          example: `${currentDate.day} ${currentDate.month} ${currentDate.year}` 
        },
        { 
          label: 'يوم/شهر/سنة هجري', 
          format: 'DD/MM/YYYY هجري', 
          example: `${currentDate.day}/${currentDate.month}/${currentDate.year} هجري` 
        },
        { 
          label: 'التاريخ الهجري الكامل', 
          format: 'DD MM YYYY', 
          example: `${currentDate.day} ${currentDate.monthName} ${currentDate.year}` 
        }
      ];
    } else {
      return [
        { 
          label: 'يوم/شهر/سنة', 
          format: 'DD/MM/YYYY', 
          example: `${currentDate.day}/${currentDate.month}/${currentDate.year}` 
        },
        { 
          label: 'يوم-شهر-سنة', 
          format: 'DD-MM-YYYY', 
          example: `${currentDate.day}-${currentDate.month}-${currentDate.year}` 
        },
        { 
          label: 'يوم.شهر.سنة', 
          format: 'DD.MM.YYYY', 
          example: `${currentDate.day}.${currentDate.month}.${currentDate.year}` 
        },
        { 
          label: 'يوم شهر سنة', 
          format: 'DD MM YYYY', 
          example: `${currentDate.day} ${currentDate.month} ${currentDate.year}` 
        },
        { 
          label: 'شهر/يوم/سنة', 
          format: 'MM/DD/YYYY', 
          example: `${currentDate.month}/${currentDate.day}/${currentDate.year}` 
        },
        { 
          label: 'التاريخ الميلادي الكامل', 
          format: 'DD MM YYYY', 
          example: `${currentDate.day} ${currentDate.monthName} ${currentDate.year}` 
        }
      ];
    }
  };

  const formats = generateFormats();

  const handleCopy = async (text: string, index: number) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, type: 'spring' }}
      className={`relative overflow-hidden rounded-3xl p-7 shadow-2xl border-0 ${colors.light} ${colors.dark} ${colors.shadow} group`}
      style={{ minHeight: 420 }}
    >
      {/* زخرفة خلفية خفيفة */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl opacity-60 pointer-events-none"></div>
      <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl opacity-40 pointer-events-none"></div>

      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-2xl font-extrabold tracking-tight ${colors.text} drop-shadow-lg`}>{title}</h3>
        <FaCalendarAlt className={`text-3xl opacity-80 ${colors.text}`} />
      </div>
      
      <div className="mb-8">
        <div className={`text-4xl font-black ${colors.text} mb-1 drop-shadow-xl`}>{date}</div>
        <div className={`text-base font-semibold ${colors.text} opacity-90 tracking-wide`}>{(title.includes('هجري') || title.includes('Hijri')) ? dateInfo.hijri.dayName : dateInfo.gregorian.dayName}</div>
      </div>

      <div className="space-y-4">
        {formats.map((format, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className={`flex items-center justify-between bg-white/20 dark:bg-white/10 rounded-2xl px-4 py-3 shadow-md hover:shadow-lg transition-all`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm mb-0.5 text-gray-900 dark:text-gray-100">{format.label}</div>
              <div className="text-xs opacity-70 mb-1 font-mono text-gray-800 dark:text-gray-200">{format.format}</div>
              <div className="font-mono text-base bg-white/40 dark:bg-white/20 rounded px-2 py-1 text-gray-900 dark:text-gray-100 select-all transition-all">
                {format.example}
              </div>
            </div>
            <button
              onClick={() => handleCopy(format.example, index)}
              className={`ml-4 p-3 rounded-full border-2 border-white/40 bg-gradient-to-br from-white/60 to-white/30 dark:from-white/20 dark:to-white/5 shadow-lg hover:scale-110 active:scale-95 transition-all duration-200 flex items-center justify-center ${
                copiedIndex === index 
                  ? 'bg-green-500 border-green-400 text-white scale-110' 
                  : 'text-gray-700 dark:text-gray-200'
              }`}
              title="نسخ التاريخ"
            >
              {copiedIndex === index ? <FaCheck className="text-xl" /> : <FaCopy className="text-xl" />}
            </button>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// Helper: Zodiac sign by month/day
function getZodiacSign(day: number, month: number, lang: string) {
  const signs = lang === 'ar'
    ? [
      'الجدي', 'الدلو', 'الحوت', 'الحمل', 'الثور', 'الجوزاء',
      'السرطان', 'الأسد', 'العذراء', 'الميزان', 'العقرب', 'القوس'
    ]
    : [
      'Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini',
      'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius'
    ];
  const lastDay = [19, 18, 20, 20, 21, 21, 22, 22, 21, 22, 21, 20];
  return day > lastDay[month - 1] ? signs[month % 12] : signs[(month - 1) % 12];
}

// Helper: Moon phase (simple algorithm)
function getMoonPhase(date: Date, lang: string) {
  // Simple moon phase calculation
  const lp = 2551443; // lunar period in seconds
  const now = date.getTime() / 1000;
  const new_moon = new Date(2001, 0, 24, 13, 35, 0).getTime() / 1000;
  const phase = ((now - new_moon) % lp) / (lp / 8);
  const phases = lang === 'ar'
    ? ['محاق', 'هلال متزايد', 'تربيع أول', 'أحدب متزايد', 'بدر', 'أحدب متناقص', 'تربيع أخير', 'هلال متناقص']
    : ['New Moon', 'Waxing Crescent', 'First Quarter', 'Waxing Gibbous', 'Full Moon', 'Waning Gibbous', 'Last Quarter', 'Waning Crescent'];
  return phases[Math.floor(phase)] || phases[0];
}

// Helper: Season by month (Northern Hemisphere)
function getSeason(month: number, lang: string) {
  if (month === 12 || month === 1 || month === 2)
    return lang === 'ar' ? 'الشتاء' : 'Winter';
  if (month >= 3 && month <= 5)
    return lang === 'ar' ? 'الربيع' : 'Spring';
  if (month >= 6 && month <= 8)
    return lang === 'ar' ? 'الصيف' : 'Summer';
  return lang === 'ar' ? 'الخريف' : 'Autumn';
}

// Helper: Local Saudi season (approximate)
function getSaudiSeason(month: number, day: number, lang: string) {
  // Simple mapping, can be expanded
  const seasons = [
    { nameAr: 'المربعانية', nameEn: 'Al-Murabaaniya', start: [12, 7], end: [1, 14] },
    { nameAr: 'الشبط', nameEn: 'Al-Shbat', start: [1, 15], end: [2, 10] },
    { nameAr: 'العقارب', nameEn: 'Al-Aqareb', start: [2, 11], end: [3, 20] },
    { nameAr: 'الحميمين', nameEn: 'Al-Hameem', start: [3, 21], end: [4, 7] },
    { nameAr: 'الذراعين', nameEn: 'Al-Dhiraain', start: [4, 8], end: [4, 20] },
    { nameAr: 'الثريا', nameEn: 'Al-Thuraya', start: [5, 7], end: [6, 6] },
    { nameAr: 'الجوزاء', nameEn: 'Al-Jawzaa', start: [6, 7], end: [7, 15] },
    { nameAr: 'المرزم', nameEn: 'Al-Mirzam', start: [7, 16], end: [8, 15] },
    { nameAr: 'سهيل', nameEn: 'Suhail', start: [8, 16], end: [9, 15] },
    { nameAr: 'الوسم', nameEn: 'Al-Wasm', start: [10, 16], end: [12, 6] },
  ];
  for (const s of seasons) {
    const [sm, sd] = s.start;
    const [em, ed] = s.end;
    if (
      (month === sm && day >= sd) ||
      (month === em && day <= ed) ||
      (month > sm && month < em) ||
      (sm > em && (month > sm || month < em))
    ) {
      return lang === 'ar' ? s.nameAr : s.nameEn;
    }
  }
  return lang === 'ar' ? 'غير محدد' : 'Unknown';
}

export default function TodayDate() {
  const { currentLanguage } = useTranslation();
  const [dateInfo, setDateInfo] = useState<DateInfo | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Check for dark mode preference
  useEffect(() => {
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModeQuery.matches);
    
    const handleDarkModeChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };
    
    darkModeQuery.addEventListener('change', handleDarkModeChange);
    return () => darkModeQuery.removeEventListener('change', handleDarkModeChange);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update date and time
  useEffect(() => {
    const updateDateTime = () => {
      try {
        const now = new Date();
        
        // Get Hijri date using moment-hijri
        const hijriMoment = moment(now);
        const hijriDate = hijriMoment.format('iD/iM/iYYYY');
        const hijriParts = hijriDate.split('/');
        
        // Get Hijri month name
        const hijriMonthNames = [
          'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة',
          'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
        ];
        
        // Get Hijri day names
        const hijriDayNames = [
          'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'
        ];
        
        // Get Gregorian date parts directly from Date object
        const gregorianDay = now.getDate().toString().padStart(2, '0');
        const gregorianMonth = (now.getMonth() + 1).toString().padStart(2, '0');
        const gregorianYear = now.getFullYear().toString();
        // Get Gregorian month name (fixed, not locale)
        const gregorianMonthNames = [
          ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
          ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        ];
        const monthIndex = now.getMonth();
        const gregorianMonthName = currentLanguage === 'ar'
          ? gregorianMonthNames[0][monthIndex]
          : gregorianMonthNames[1][monthIndex];
        
        // Get Gregorian day names
        const gregorianDayNames = currentLanguage === 'ar' ? 
          ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'] :
          ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        // Get time
        const time = now.toLocaleTimeString(currentLanguage === 'ar' ? 'ar-SA' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
        
        // Get timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        const newDateInfo: DateInfo = {
          hijri: {
            day: hijriParts[0] || '1',
            month: hijriParts[1] || '1',
            year: hijriParts[2] || '1446',
            fullDate: hijriDate,
            dayName: hijriDayNames[now.getDay()] || 'الأحد',
            monthName: hijriMonthNames[parseInt(hijriParts[1]) - 1] || 'محرم'
          },
          gregorian: {
            day: gregorianDay,
            month: gregorianMonth,
            year: gregorianYear,
            fullDate: `${gregorianDay}/${gregorianMonth}/${gregorianYear}`,
            dayName: gregorianDayNames[now.getDay()] || 'Sunday',
            monthName: gregorianMonthName || 'January'
          },
          time,
          timezone
        };
        
        setDateInfo(newDateInfo);
      } catch (error) {
        console.error('Error updating date:', error);
        // Fallback date info
        const fallbackDateInfo: DateInfo = {
          hijri: {
            day: '15',
            month: '04',
            year: '1446',
            fullDate: '15/04/1446',
            dayName: 'الأربعاء',
            monthName: 'رجب'
          },
          gregorian: {
            day: '23',
            month: '10',
            year: '2024',
            fullDate: '23/10/2024',
            dayName: 'Wednesday',
            monthName: 'October'
          },
          time: '12:00:00',
          timezone: 'Asia/Riyadh'
        };
        setDateInfo(fallbackDateInfo);
      }
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, [currentLanguage]);

  if (!dateInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">جاري تحميل التاريخ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className="flex items-center space-x-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
            >
              <AiFillHome className="text-2xl" />
              <span className="font-semibold">{currentLanguage === 'ar' ? 'الرئيسية' : 'Home'}</span>
            </Link>
            
      
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-24 pb-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-gray-800 dark:text-white mb-4">
            {currentLanguage === 'ar' ? 'تاريخ اليوم' : 'Today\'s Date'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            {currentLanguage === 'ar' ? 'التاريخ الهجري والميلادي مع صيغ مختلفة للنسخ' : 'Hijri and Gregorian dates with different formats for copying'}
          </p>
          
          {/* Current Time Display */}
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full shadow-lg"
          >
            <div className="flex items-center space-x-3">
              <FaClock className="text-2xl" />
              <div>
                <div className="text-2xl font-bold">{dateInfo.time}</div>
                <div className="text-sm opacity-90">{dateInfo.timezone}</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Date Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Hijri Date Card */}
          <DateFormatCard
            title={currentLanguage === 'ar' ? 'التاريخ الهجري' : 'Hijri Date'}
            date={dateInfo.hijri.fullDate}
            colorScheme="primary"
            // lang: 'ar' | 'en';
            dateInfo={dateInfo}
          />
          
          {/* Gregorian Date Card */}
          <DateFormatCard
            title={currentLanguage === 'ar' ? 'التاريخ الميلادي' : 'Gregorian Date'}
            date={dateInfo.gregorian.fullDate}
            colorScheme="secondary"
            // lang: 'ar' | 'en';
            dateInfo={dateInfo}
          />
        </div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {/* Day & Month Information (merged) */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-green-500 text-white p-3 rounded-lg">
                <FaCalendarAlt />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                {currentLanguage === 'ar' ? 'معلومات اليوم والشهر' : 'Day & Month Information'}
              </h3>
            </div>
            <div className="space-y-3">
              {/* Day Name (once) */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'اليوم:' : 'Day:'}
                </span>
                <span className="font-semibold text-gray-800 dark:text-white">
                  {dateInfo.gregorian.dayName}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'الشهر الهجري:' : 'Hijri Month:'}
                </span>
                <span className="font-semibold text-gray-800 dark:text-white">
                  {dateInfo.hijri.monthName}
                </span>
              </div>
              {/* Gregorian Day & Month */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'الشهر الميلادي:' : 'Gregorian Month:'}
                </span>
                <span className="font-semibold text-gray-800 dark:text-white">
                  {dateInfo.gregorian.monthName}
                </span>
              </div>
              {/* Zodiac Sign */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'البرج الفلكي:' : 'Zodiac Sign:'}
                </span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-300">
                  {getZodiacSign(Number(dateInfo.gregorian.day), Number(dateInfo.gregorian.month), currentLanguage)}
                </span>
              </div>
              {/* Moon Phase */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'وضع القمر:' : 'Moon Phase:'}
                </span>
                <span className="font-semibold text-purple-600 dark:text-purple-300">
                  {getMoonPhase(new Date(), currentLanguage)}
                </span>
              </div>
              {/* Season */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'الفصل المناخي:' : 'Season:'}
                </span>
                <span className="font-semibold text-pink-600 dark:text-pink-300">
                  {getSeason(Number(dateInfo.gregorian.month), currentLanguage)}
                </span>
              </div>
              {/* Saudi Season */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  {currentLanguage === 'ar' ? 'الموسم المحلي:' : 'Local Season:'}
                </span>
                <span className="font-semibold text-yellow-600 dark:text-yellow-300">
                  {getSaudiSeason(Number(dateInfo.gregorian.month), Number(dateInfo.gregorian.day), currentLanguage)}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
} 